import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AddItemButton, AddItemDropdown } from "@components/addItem";
import { SettingsDrawer } from "@components/Navbar/Settings";
import ItemViewColumnValues, { SHARED_COLUMNS_ID } from "./ItemViewColumnValues";
import useItemLocations from "../../hooks/api/useItemLocations";
import useItemName from "../../hooks/api/useItemName";
import useUnsharedBoardColumns, {
  columnValueToColumn,
} from "../../hooks/api/useUnsharedBoardColumns";
import { Column } from "./table.data.interface";
import type { ColumnValueType } from "../../services/monday/monday-query-interface";
import { IconButton, Loader, Skeleton, Tooltip } from "@vibe/core";
import { Help, Retry } from "@vibe/icons";
import useUser from "../../hooks/context/userHook";
import logger from "../../lib/logger";
import { useErrorWebsocket } from "../../hooks/services";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import useFireNudgeTrigger from "../../hooks/api/useFireNudgeTrigger";
import { setItemConnected } from "../../redux/slices/itemConnectedSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";
import { extractColumnValue } from "../../utils/columnValueExtractor";
import { IoSync } from "react-icons/io5";
import OtherBoardsColumns from "./OtherBoardsColumns";
import ActivityLog from "./ActivityLog";
// Read-only mode: creating updates is disabled for now. Kept for easy re-enable.
// import UpdateComposer from "./ActivityLog/composer/UpdateComposer";
import { ITEM_VIEW_COLUMN } from "./itemViewLayout";
import BoardsHeader from "./BoardsHeader";
import AddItemProgressBanner from "./AddItemProgressBanner";
import useSimulatedProgress from "../../hooks/ui/useSimulatedProgress";
import useAddItemInProgress from "../../hooks/ui/useAddItemInProgress";
import useItemMergeStream from "../../hooks/services/useItemMergeStream";
import { useBigAccount, useSharedColumnsTourModal } from "../../hooks/ui";
import TourComponent from "@components/Tour/CustomTourComponent";
import { tourStepsBig, tourStepsSmall } from "../../data/constants/tour-steps/add_item_steps";
import ButtonComponent from "@components/Button";
import { EXTERNAL_LINKS } from "@data/externalLinks";
import useMondayContext from "@hooks/context/useMondayContext";
import { isSimbManagedColumnTitle } from "../../utils/simbManagedColumns";
import CurrentBoardColumns from "./CurrentBoardColumns";
import { ShareColumnProvider } from "./ShareColumn/ShareColumnContext";
import { BACKFILL_WRITES_PER_MINUTE, type ShareColumnResult } from "./ShareColumn/shareColumn";
import {
  FORCE_PUSH_SLOW_FALLBACK_MS,
  type RegisterForcePush,
} from "./ItemViewDiscrepancy/useForcePushTracker";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { SLOW_RESYNC_COLUMN_TYPES } from "../../utils/resync/itemResync/itemResyncUtils";
import { analytics } from "../../utils/segment/analyticsService";

// Check if a required scope is missing from approved scopes
const getMissingScopes = (requiredScopes: string[], approvedScopes: string[]): string[] => {
  return requiredScopes.filter(scope => !approvedScopes.includes(scope));
};

// Banner component for missing scopes
const MissingScopesBanner = ({ missingScopes }: { missingScopes: string[] }) => {
  if (missingScopes.length === 0) return null;

  return (
    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
      <p className="text-sm text-blue-900">
        Some features need new permissions. Ask an admin on your monday account to open Administration &gt; Apps &gt; Same Item Multiple Boards, click Update, and accept the permissions.
      </p>
    </div>
  );
};

// Constants
const HEADER_MARGIN_BOTTOM = "mb-[1.875rem]";
const SKELETON_ITEM_NAME_WIDTH = 200;
const SKELETON_ITEM_NAME_HEIGHT = 40;
// Long enough to read the share summary and reach "Review columns" before the banner hides.
const SHARE_SUCCESS_DISPLAY_MS = 8000;

interface ItemViewSectionProps {
  tourState: boolean;
}

// Sub-components for better readability
const ItemNameHeader = ({
  itemName,
  hasDiscrepancies,
  handleResynchronizeAllColumnsModal,
}: {
  itemName: string;
  hasDiscrepancies: boolean;
  handleResynchronizeAllColumnsModal: (value: boolean) => void;
}) => (
  <div className="flex overflow-hidden gap-2 items-center w-full item-name-wrapper">
    <div className="overflow-hidden flex-1 min-w-0">
      <Tooltip content={itemName} referenceWrapperClassName="min-w-0 w-full overflow-hidden block">
        <div className="overflow-hidden w-full min-w-0">
          {itemName === "" ? (
            <Skeleton
              type="text"
              size="h2"
              width={SKELETON_ITEM_NAME_WIDTH}
              height={SKELETON_ITEM_NAME_HEIGHT}
            />
          ) : (
            <h2 className="!font-poppins font-medium truncate block">{itemName}</h2>
          )}
        </div>
      </Tooltip>
    </div>

    {hasDiscrepancies && (
      <Tooltip content="Resynchronize all columns">
        <div className="flex justify-center items-center">
          <IconButton
            kind="tertiary"
            icon={IoSync}
            size="medium"
            onClick={() => handleResynchronizeAllColumnsModal(true)}
          />
        </div>
      </Tooltip>
    )}
  </div>
);

const ActionButtons = ({
  stepsEnabled,
  hideAddItemButton,
  isLoadingItems,
}: {
  stepsEnabled: boolean;
  hideAddItemButton: boolean;
  isLoadingItems: boolean;
}) => {
  const handleOpenHelp = () => {
    window.open(EXTERNAL_LINKS.HOW_TO_USE, "_blank");
  };

  return (
    <div className="flex gap-2">
      {isLoadingItems ? (
        <Skeleton type="rectangle" width={126.21} height={40} />
      ) : (
        <>{!hideAddItemButton && <AddItemButton centered={false} tourState={stepsEnabled} />}</>
      )}

      <IconButton
        icon={Help}
        kind="secondary"
        tooltipContent="Guided Tour"
        onClick={handleOpenHelp}
        size="medium"
      />
      <SettingsDrawer />
    </div>
  );
};

const ErrorState = ({ retryLoading, onRetry }: { retryLoading: boolean; onRetry: () => void }) => (
  <div className="flex flex-col gap-2 justify-center items-center">
    {retryLoading ? (
      <Loader size="small" color="primary" />
    ) : (
      <>
        <IconButton
          onClick={onRetry}
          className="cursor-pointer"
          icon={Retry}
          size="small"
          kind="primary"
        />
        <span>An error occurred. Please try again.</span>
      </>
    )}
  </div>
);

const NoItemsState = ({ tourState }: { tourState: boolean }) => {
  const [open, setOpen] = useState(false);
  const bigAccount = useBigAccount();
  const fireNudge = useFireNudgeTrigger();
  const { appFeatureType } = useMondayContext();
  const { currentBoard, itemIds } = useBoardAndItems();

  const handleShareClick = () => {
    // A2: "share item" clicked in an item view (target = this item)
    if (appFeatureType === "AppFeatureItemView") {
      fireNudge({
        source: "share_opened_item",
        itemid: itemIds?.[0],
        boardid: currentBoard,
        viewType: "item_view",
      });
    }
    setOpen(!open);
  };

  return (
    <div className="flex flex-col gap-2 justify-center items-center mt-4">
      <h4 className="!font-figtree text-[22px] font-medium">
        This item doesn't live on other boards yet!
      </h4>

      <div className="relative mt-8">
        <ButtonComponent
          text="Share to another board"
          onClick={handleShareClick}
          className="add-item-button"
          id="add_item_step1"
          relative
        />
        {open && (
          <AddItemDropdown
            centered
            setOpen={setOpen}
            fullWidth={false}
            bigAccount={bigAccount}
            stepsEnabled={tourState}
          />
        )}
      </div>
    </div>
  );
};

const ItemViewSection = ({ tourState }: ItemViewSectionProps) => {
  const { isLoading, error, tableData, itemIds, mutateItemLocations, refreshItemLocationsQuietly } =
    useItemLocations();
  const { userId, accountId } = useUser();
  const { currentBoard, itemIds: currentItemIds } = useBoardAndItems();
  const { itemName } = useItemName(itemIds);
  const { approvedScopes, requiredScopes } = useMondayContext();
  // The item has no linked items across boards, so ItemViewSection renders its
  // empty state (NoItemsState). Same signal as useIsItemViewEmpty(), reusing
  // the tableData already loaded here. The Activity Log + composer are gated on
  // this so neither shows in the empty state (an empty item still has a
  // selected itemIds[0], which is why itemIds alone wasn't enough).
  const isItemViewEmpty = tableData?.noLinkedItems === true;
  const [hasDiscrepancies, setHasDiscrepancies] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [stepsEnabled, setStepsEnabled] = useState(tourState);
  const [showResynchronizeModal, setShowResynchronizeModal] = useState(false);

  const missingScopes = getMissingScopes(requiredScopes, approvedScopes);

  const bigAccount = useBigAccount();
  const targetBoardId = useSelector((state: RootState) => state.targetBoardName.targetBoardId);
  const { sharedColumnsTour } = useSharedColumnsTourModal(
    targetBoardId && currentBoard ? [currentBoard, targetBoardId] : [],
    stepsEnabled,
  );

  const handleResynchronizeAllColumnsModal = (value: boolean) => {
    setShowResynchronizeModal(value);
  };

  const otherLinkedBoards = useMemo(
    () =>
      tableData?.linkedBoards?.filter((item) => item.boardId !== currentBoard?.toString()) ?? [],
    [tableData?.linkedBoards, currentBoard],
  );

  // Buffer the linked boards so that during revalidation (when tableData briefly
  // becomes undefined and the lists become []), the child hook still receives the
  // previous valid board list — keeping the SWR cache key stable.
  const bufferedLinkedBoardsRef = useRef(otherLinkedBoards);
  if (otherLinkedBoards.length > 0) {
    bufferedLinkedBoardsRef.current = otherLinkedBoards;
  }

  // Every board the item lives on, the current one included: its unshared columns are the
  // ones the user can add to the other boards.
  const allLinkedBoards = useMemo(() => tableData?.linkedBoards ?? [], [tableData?.linkedBoards]);
  const bufferedAllLinkedBoardsRef = useRef(allLinkedBoards);
  if (allLinkedBoards.length > 0) {
    bufferedAllLinkedBoardsRef.current = allLinkedBoards;
  }
  const currentLinkedBoard = bufferedAllLinkedBoardsRef.current.find(
    (board) => board.boardId === currentBoard?.toString(),
  );

  const {
    columnsByBoardId,
    isLoading: isLoadingUnshared,
    error: unsharedColumnsError,
    mutate: mutateUnsharedColumns,
    refreshQuietly: refreshUnsharedColumnsQuietly,
  } = useUnsharedBoardColumns(
    bufferedAllLinkedBoardsRef.current.length > 0 ? bufferedAllLinkedBoardsRef.current : null,
  );

  const sharedColumns = useMemo(
    () => tableData?.columns?.filter((item) => !isSimbManagedColumnTitle(item.title)) ?? [],
    [tableData?.columns],
  );

  const unsharedColumnsByBoard = useMemo(() => {
    const sharedTitles = new Set(sharedColumns.map((c) => c.title));
    const result: Record<string, typeof sharedColumns> = {};
    for (const [boardId, columns] of Object.entries(columnsByBoardId)) {
      result[boardId] = columns.filter((c) => !sharedTitles.has(c.title));
    }
    return result;
  }, [columnsByBoardId, sharedColumns]);

  // tableData.columns can list a column that only one board has; those are not "shared" for
  // the current board's purposes, so only titles on two or more boards rule a column out.
  const currentBoardOnlyColumns = useMemo(() => {
    if (!currentLinkedBoard) return [];
    const syncedTitles = new Set(
      sharedColumns.filter((c) => c.boards.length > 1).map((c) => c.title),
    );
    return (columnsByBoardId[currentLinkedBoard.boardId] ?? []).filter(
      (c) => !syncedTitles.has(c.title),
    );
  }, [columnsByBoardId, sharedColumns, currentLinkedBoard]);

  const registerForcePushRef = useRef<RegisterForcePush | null>(null);

  const handleColumnShared = useCallback(
    (result: ShareColumnResult, columnType: string) => {
      const boardNames = result.created.map((board) => board.boardName).join(" and ");
      const added = `"${result.name}" added to ${boardNames}.`;
      const copy = result.valueCopy;

      const message =
        copy.status === "queued"
          ? `${added} Copying values to ${copy.itemCount.toLocaleString()} connected item${
              copy.itemCount === 1 ? "" : "s"
            } in the background.`
          : copy.status === "none"
            ? `${added} No connected items to copy values to.`
            : copy.status === "unavailable"
              ? `${added} Values couldn't be copied right now; they sync as items are edited.`
              : `${added} Values couldn't be copied; use Resync on the column.`;

      MondayApiQueryInstance.mondayToastNotification(
        message,
        copy.status === "failed" || copy.status === "unavailable" ? "info" : "success",
        5000,
      );

      if (copy.status === "queued") {
        // One job per item, paced against the account's monday budget: wait for the socket
        // well past the expected duration before falling back to a plain refresh.
        const expectedMs = (copy.itemCount / BACKFILL_WRITES_PER_MINUTE) * 60_000;
        registerForcePushRef.current?.(
          copy.requestId,
          [copy.columnKey],
          SLOW_RESYNC_COLUMN_TYPES.has(columnType),
          Math.max(FORCE_PUSH_SLOW_FALLBACK_MS, expectedMs * 2 + 60_000),
        );
      }

      analytics.track("item_view_column_shared", {
        groupId: accountId,
        platform: "web",
        columnType,
        boardCount: result.created.length,
        valueCopy: result.valueCopy.status,
        itemCount: result.valueCopy.status === "queued" ? result.valueCopy.itemCount : 0,
        timestamp: new Date().toISOString(),
      });

      void mutateUnsharedColumns();
      void mutateItemLocations();
    },
    [accountId, mutateUnsharedColumns, mutateItemLocations],
  );

  // A merge runs from the Settings modal, in another iframe, and its outcome arrives over the
  // notification socket whether or not Settings is still open. Either side of the merge refetches
  // what it shows here; a failed merge changed nothing, so it needs no refetch.
  useItemMergeStream({
    userId,
    accountId,
    onCompleted: (event) => {
      if (event.state === "failed") return;
      const shown = (currentItemIds ?? []).map(String);
      if (
        !shown.includes(String(event.currentItemId)) &&
        !shown.includes(String(event.otherItemId))
      )
        return;
      void mutateItemLocations();
      void mutateUnsharedColumns();
    },
  });

  const shareColumnContext = useMemo(
    () => ({
      linkedBoards: bufferedAllLinkedBoardsRef.current,
      currentBoardId: currentBoard?.toString() ?? "",
      onShared: handleColumnShared,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLinkedBoards, currentBoard, handleColumnShared],
  );

  const handleMutateItemLocations = (
    columnType?: string,
    columnTitle?: string,
    latestValue?: any,
  ) => {
    const isMultiColumnUpdate = columnType === undefined && columnTitle === undefined;

    if (isMultiColumnUpdate) {
      const updatedColumns = tableData?.columns?.map((column) => {
        const key = `${column.type}_${column.title}`;
        const entry = latestValue?.[key];
        if (!entry) return column;

        // Multi-column payload nests the raw value under `data`, so we pass
        // `entry.data` to match the shape `extractColumnValue` expects (`.value...`).
        const newValue = extractColumnValue(column.type, entry.data ?? entry);
        return { ...column, value: newValue };
      });

      mutateItemLocations(
        {
          ...tableData,
          columns: updatedColumns,
        },
        { revalidate: false },
      );
      return;
    }

    const newValue = extractColumnValue(columnType as string, latestValue);

    mutateItemLocations(
      {
        ...tableData,
        columns: tableData?.columns?.map((column) =>
          columnType === column.type && columnTitle === column.title
            ? { ...column, value: newValue }
            : column,
        ),
      },
      { revalidate: false },
    );
  };

  const handleRevalidateItemLocations = async () => {
    await mutateItemLocations();
  };

  const handlePatchSharedColumn = (
    columnType: string,
    columnTitle: string,
    patch: Partial<Column>,
  ) => {
    mutateItemLocations(
      {
        ...tableData,
        columns: tableData?.columns?.map((column) =>
          column.type === columnType && column.title === columnTitle
            ? { ...column, ...patch }
            : column,
        ),
      },
      { revalidate: false },
    );
  };

  // Shows the value monday returned from the write straight away, then refetches quietly.
  const handleUnsharedValueSaved = async (saved: boolean, written?: ColumnValueType) => {
    if (!saved || !written || !currentLinkedBoard) {
      await refreshUnsharedColumnsQuietly();
      return;
    }
    const { boardId, boardName } = currentLinkedBoard;
    const updated = columnValueToColumn(written, boardId, boardName);
    await mutateUnsharedColumns(
      (current) => ({
        ...current,
        [boardId]: (current?.[boardId] ?? []).map((column) =>
          column.columnId === updated.columnId ? updated : column,
        ),
      }),
      { revalidate: false },
    );
    void refreshUnsharedColumnsQuietly();
  };

  const dispatch = useDispatch();

  const handleStepsEnabled = (value: boolean) => {
    setStepsEnabled(value);
  };

  const handleHasDiscrepancies = (value: boolean) => {
    setHasDiscrepancies(value);
  };

  // Effect for managing item connection status
  useEffect(() => {
    if (tableData && tableData.noLinkedItems) {
      dispatch(setItemConnected(false));
    } else {
      dispatch(setItemConnected(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  useErrorWebsocket(
    userId ? String(userId) : "",
    accountId ? String(accountId) : "",
    itemIds?.[0]?.toString() || "",
  );

  const { isAddingItem, hasFailed: addItemFailed } = useAddItemInProgress();
  const {
    progress,
    isVisible: showProgressBanner,
    isCompleted,
    hasFailed,
  } = useSimulatedProgress(isAddingItem, addItemFailed, SHARE_SUCCESS_DISPLAY_MS);

  // The share success state: which board the item just landed on and how many columns matched
  // there by name + type. The count stays undefined until the reloaded item view lists that
  // board, so the banner never claims "0 columns" off stale data.
  const sharedBoardName = useSelector((state: RootState) => state.targetBoardName.targetBoardName);
  const sharedBoardId = allLinkedBoards.find(
    (board) => board.boardName === sharedBoardName,
  )?.boardId;
  const syncingColumnCount = sharedBoardId
    ? sharedColumns.filter(
        (column) =>
          column.boards.length > 1 &&
          column.boards.some(
            (board) => typeof board !== "string" && board.boardId === sharedBoardId,
          ),
      ).length
    : undefined;

  const handleReviewColumns = () => {
    document
      .getElementById(SHARED_COLUMNS_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleRetry = async () => {
    try {
      setRetryLoading(true);
      await mutateItemLocations();
    } catch (error: any) {
      logger.error("Error handling item locations retry", error);
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <>
      <section className={ITEM_VIEW_COLUMN}>
        {/* Header Section */}
        <div>
          <div className={`flex gap-2 justify-between ${HEADER_MARGIN_BOTTOM}`}>
            <ItemNameHeader
              itemName={itemName}
              hasDiscrepancies={hasDiscrepancies}
              handleResynchronizeAllColumnsModal={handleResynchronizeAllColumnsModal}
            />
            <ActionButtons
              stepsEnabled={stepsEnabled}
              hideAddItemButton={tableData && tableData?.noLinkedItems ? true : false}
              isLoadingItems={isLoading}
            />
          </div>
        </div>

        {/* Add Item Progress Banner */}
        {showProgressBanner && (
          <AddItemProgressBanner
            progress={progress}
            isCompleted={isCompleted}
            hasFailed={hasFailed}
            sharedBoardName={sharedBoardName || undefined}
            syncingColumnCount={syncingColumnCount}
            onReviewColumns={sharedBoardId ? handleReviewColumns : undefined}
          />
        )}

        {/* Missing Scopes Banner */}
        <MissingScopesBanner missingScopes={missingScopes} />

        {/* Content Section */}
        <div className={`mx-auto`}>
          {error ? (
            <ErrorState retryLoading={retryLoading} onRetry={handleRetry} />
          ) : tableData && tableData.noLinkedItems && !showProgressBanner ? (
            <NoItemsState tourState={stepsEnabled} />
          ) : (
            !tableData?.noLinkedItems && (
              <ShareColumnProvider value={shareColumnContext}>
                {/* Boards Header */}
                <BoardsHeader
                  isLoading={isLoading}
                  tableData={tableData!}
                  currentBoard={currentBoard!}
                  userId={userId!}
                />

                <ItemViewColumnValues
                  tableData={tableData}
                  isLoading={isLoading}
                  currentItemIds={currentItemIds!}
                  handleMutateItemLocations={handleMutateItemLocations}
                  handleRevalidateItemLocations={handleRevalidateItemLocations}
                  handlePatchSharedColumn={handlePatchSharedColumn}
                  handleRefreshAfterEdit={refreshItemLocationsQuietly}
                  handleHasDiscrepancies={handleHasDiscrepancies}
                  showResynchronizeModal={showResynchronizeModal}
                  handleResynchronizeAllColumnsModal={handleResynchronizeAllColumnsModal}
                  registerForcePushRef={registerForcePushRef}
                />

                {currentLinkedBoard && (
                  <CurrentBoardColumns
                    columns={currentBoardOnlyColumns}
                    itemId={currentLinkedBoard.itemId}
                    boardId={Number(currentLinkedBoard.boardId)}
                    onValueSaved={handleUnsharedValueSaved}
                  />
                )}

                {/* Connected Boards and Columns that are not shared in other boards */}
                <div>
                  <OtherBoardsColumns
                    data={bufferedLinkedBoardsRef.current}
                    unsharedColumnsByBoard={unsharedColumnsByBoard}
                    isLoading={isLoadingUnshared && Object.keys(columnsByBoardId).length === 0}
                    error={unsharedColumnsError}
                    onRetry={() => mutateUnsharedColumns()}
                  />
                </div>
              </ShareColumnProvider>
            )
          )}
        </div>

        {itemIds?.[0] && !isItemViewEmpty ? (
          <div className="mt-[1.875rem] pb-24">
            <ActivityLog itemId={String(itemIds[0])} />
          </div>
        ) : null}
      </section>

      {/* Read-only mode: the fixed update composer is disabled for now.
          Uncomment (and the import above) to re-enable creating updates. */}
      {/* {itemIds?.[0] && !isItemViewEmpty ? <UpdateComposer /> : null} */}

      <TourComponent
        tourSteps={bigAccount ? tourStepsBig : tourStepsSmall}
        tourState={stepsEnabled}
        handleStepsEnabled={handleStepsEnabled}
        sharedColumnsTour={sharedColumnsTour}
      />
    </>
  );
};

export default ItemViewSection;
